import { useState, useRef, useEffect, useMemo } from 'react';
import prsService from '../../service/prs';
import { flushSync } from 'react-dom';
import Swal from 'sweetalert2'
import { Accordion } from 'react-bootstrap';

const ChatBox = ({ runId, productId, nodeId }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const textareaRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user'))
  const [isBotResponding, setIsBotResponding] = useState(false);
  // Scroll 
  const bottomRef = useRef(null)
  // Chat Recommended Questions
  const [defaultQuestions, setDefaultQuestions] = useState([]);

  // Questions 
  const [questionSent, setQuestionSent] = useState(false);
  const [showAccordion, setShowAccordion] = useState(false); // Start hidden, will show based on message count
  
  // Loading Messages - now used to show loading animation while waiting for bot response
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  useEffect(() => {
    startThread();
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 100)}px`;
    }
  }, [inputText]);

  // Scroll useEffect - now triggers on showAccordion changes too
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, showAccordion])

  // Chat Questions
  useEffect(()=> {
    fetchDefaultQuestions();
  }, []);

  const fetchDefaultQuestions = async () => {
    try {
      const response = await fetch('https://api.gennext.bio/api/v1/coach/default-questions');
      const data = await response.json();
      setDefaultQuestions(data.questions);
    } catch (error) {
      console.error('Failed to fetch default questions:', error);
    }
  };

  const handleRecommendedClick = async (questionText) => {
    if (!connected || isBotResponding) return;

    setQuestionSent(true);
    setShowAccordion(false);
    setIsLoadingMessages(true); // Show loading while waiting for bot response
  
    const userMessage = { text: questionText, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
  
    let botIndex = null;
    flushSync(() => {
      setMessages(prev => {
        const updated = [...prev, { text: '', sender: 'bot' }];
        botIndex = updated.length - 1;
        return updated;
      });
    });
  
    try {
      const response = await fetch(`https://api.gennext.bio/api/v1/coach/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          message_content: userMessage.text,
        }),
      });
  
      if (!response.body) throw new Error('No response body.');
  
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let accumulatedMessage = '';
  
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
  
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
  
          let parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
  
          for (let part of parts) {
            if (!part.trim()) continue;
  
            if (part.startsWith(':')) part = part.substring(1).trim();
            if (!part.trim()) continue;
  
            if (part.startsWith('data: ')) {
              const jsonData = part.substring(6).trim();
  
              try {
                const data = JSON.parse(jsonData);
  
                if (data.event === 'ongoing' && data.delta) {
                  // Always hide loading on first chunk
                  setIsLoadingMessages(false);
                  
                  accumulatedMessage += data.delta;
                  if (botIndex !== null) {
                    updateBotMessage(botIndex, formatMarkdown(accumulatedMessage));
                  }
                }
  
                if (data.event === 'complete') {
                  setIsBotResponding(false);
                  setIsLoadingMessages(false); // Ensure loading is hidden when complete
                }
              } catch (error) {
                console.error('JSON parse error:', error, jsonData);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (botIndex !== null) {
        updateBotMessage(botIndex, `Error: ${error.message}`);
      }
      setIsBotResponding(false);
      setIsLoadingMessages(false); // Hide loading on error
    }
  };
  

  const groupedQuestions = useMemo(() => {
    const groups = {};
    defaultQuestions.forEach(q => {
      if (!groups[q.category]) groups[q.category] = [];
      groups[q.category].push(q);
    });
    return Object.entries(groups); // [[category, [questions]], ...]
  }, [defaultQuestions]);

  // Thread Başlar
  const startThread = async () => {
    try {
      setLoading(true);
      setIsLoadingMessages(true); // Show loading while thread starts
      const { data } = await prsService.startThread(
        `https://api.gennext.bio/api/v1/coach/start-thread`,
        productId,
        nodeId,
        runId
      );

      setCurrentThreadId(data.thread_id);
      setConnected(true);

      const reversedMessages = (data.messages || []).reverse();

      const processedMessages = reversedMessages.map((msg, index) => ({
        text: formatMarkdown(msg),
        sender: index % 2 === 0 ? 'bot' : 'user'
      }));

      setMessages(processedMessages);
      
      // Show accordion if there's exactly 1 bot message (initial state)
      const botMessages = processedMessages.filter(msg => msg.sender === 'bot');
      setShowAccordion(botMessages.length === 1);
      setQuestionSent(botMessages.length > 0);
      
    } catch (error) {
      console.error('Connection error:', error);
      setErrorMsg('Failed to connect: ' + (error.response?.data || error.message));
    } finally {
      setLoading(false);
      setIsLoadingMessages(false); // Hide loading
    }
  };

  // Yeniden Thread'i Başlat (Button triggers)
  const renewThread = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Start New Conversation',
      text: 'Are you sure you\'d like to start a new conversation? Your current conversation will be lost',
      icon: 'warning',
      iconColor: '#f59e0b',
      showCancelButton: true,
      confirmButtonColor: '#101f3d',
      cancelButtonColor: '#e93645',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    })
    
    if (!isConfirmed) return;
    
    try {
      setLoading(true);
      setMessages([]);
      setConnected(false);
      setErrorMsg('');
      setQuestionSent(false);
      setShowAccordion(true); // Reset accordion to show when starting a new thread
      const response = await fetch(`https://api.gennext.bio/api/v1/coach/renew-thread`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify({
          productId,
          nodeId,
          runId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to renew thread');
      }
  
      // Yeni thread al ve başlat
      await startThread();
    } catch (error) {
      console.error('Error renewing thread:', error);
      setErrorMsg('New Conversation Failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || !connected || isBotResponding) return;

    setIsBotResponding(true);
    setIsLoadingMessages(true); // Show loading while waiting for bot response

    const userMessage = { text: inputText.trim(), sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    let botIndex = null;

    flushSync(() => {
      setMessages(prev => {
        const updated = [...prev, { text: '', sender: 'bot' }];
        botIndex = updated.length - 1;
        return updated;
      });
    });

    try {
      const response = await fetch(`https://api.gennext.bio/api/v1/coach/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          message_content: userMessage.text,
        }),
      });

      if (!response.body) {
        throw new Error('No response body.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let accumulatedMessage = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          let parts = buffer.split('\n\n');
          buffer = parts.pop() || '';

          for (let part of parts) {
            if (!part.trim()) continue;

            if (part.startsWith(':')) {
              part = part.substring(1).trim();
            }

            if (!part.trim()) continue;

            if (part.startsWith('data: ')) {
              const jsonData = part.substring(6).trim();

              try {
                const data = JSON.parse(jsonData);

                if (data.event === 'ongoing' && data.delta) {
                  // Always hide loading on first chunk
                  setIsLoadingMessages(false);
                  
                  accumulatedMessage += data.delta;
                  if (botIndex !== null) {
                    updateBotMessage(botIndex, formatMarkdown(accumulatedMessage));
                  }
                }

                if (data.event === 'complete') {
                  console.log('Bot cevabı tamamlandı.');
                  setIsBotResponding(false);
                  setIsLoadingMessages(false); // Ensure loading is hidden when complete
                }
              } catch (error) {
                console.error('JSON parse error:', error, jsonData);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (botIndex !== null) {
        updateBotMessage(botIndex, `Error: ${error.message}`);
      }
      setIsBotResponding(false);
      setIsLoadingMessages(false); // Hide loading on error
    }
  };


  const updateBotMessage = (index, text) => {
    setMessages((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text };
      return updated;
    });
  };

  const formatMarkdown = (text) => {
    if (!text) return '';

    let formatted = text;

    // Kaynak referansları temizle
    formatted = formatted.replace(/【\d+:\d+†source】/g, '');
    formatted = formatted.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    formatted = formatted.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    formatted = formatted.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    formatted = formatted.replace(/\*\*(.*?)\*\*/gim, '<b>$1</b>');
    formatted = formatted.replace(/^- (.*$)/gim, '<li>$1</li>');
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted.trim();
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Function to toggle accordion and ensure scrolling
  const toggleAccordion = () => {
    setShowAccordion(!showAccordion);
    // We don't need to explicitly call scrollIntoView here because the useEffect will handle it
  };
  
  return (
    <div className="chatbox-container">
      <div className="card border-card">
        <div className="d-flex justify-content-between align-items-center px-4 py-2 bg-heading">
          <h3 className="mb-0">
            Gennext Wellness Coach
          </h3>
          {/* New Conversation Button*/}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={renewThread}
            disabled={loading || isBotResponding}
          >
            <i className="fas fa-sync-alt me-1" style={{ fontSize: '1.5rem' }}></i>
          </button>
        </div>

        <div className="card-body" style={{ minHeight:'400px', overflowY: 'auto' }}>
          {/*Loading Chatbox*/}
          {loading && <div className={` h-auto text-center  py-2 `}>
            <div className='spinner-grow text-primary m-auto py-2' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>}
          {errorMsg && <div className="text-danger text-center">{errorMsg}</div>}

          {/* Messages */}
          {!loading && messages.map((msg, index) => (
            <div
              key={index}
              className={`w-100 ${msg.sender === 'bot' ? 'pull-left' : 'pull-right text-end'}`}
            >
              <div
                className={`mb-2 d-inline-block p-3 border rounded ${msg.sender === 'bot' ? 'bg-light mt-5 list-unstyled' : 'bg-brand mt-5 text-white'}`}
                style={{ fontSize: '16px', color: '#101f3d' }}
                dangerouslySetInnerHTML={{ __html: msg.text }}  
              >
              </div>
            </div>
          ))}

          {/* Loading Answer - Now visible when waiting for bot responses */}      
          {!loading && isLoadingMessages && (
            <div className="text-center py-4 text-muted">Loading<span className="dot-anim">...</span></div>
          )}
          
          {/* Show accordion when there's exactly 1 bot message or when not questionSent yet */}
          {!loading && messages.length > 0 && !questionSent && groupedQuestions.length > 0 && (
            <div className="chat-accordion mt-4 mb-4">
              <div className="fw-bold mb-2 text-muted">Recommended Questions</div>
              <Accordion defaultActiveKey={null}>
                {groupedQuestions.map(([category, questions], idx) => (
                  <Accordion.Item eventKey={String(idx)} key={category}>
                    <Accordion.Header>{category}</Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex flex-wrap gap-2">
                        {questions.map((q, i) => (
                          <button
                            key={i}
                            className="btn btn-outline btn-accordion-hover text-start btn-sm"
                            onClick={() => handleRecommendedClick(q.question)}
                            disabled={isBotResponding}
                          >
                            {q.question}
                          </button>
                        ))}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          )}

          {/* Toggle-able Accordion after questions are sent */}
          {!loading && messages.length > 0 && questionSent && showAccordion && groupedQuestions.length > 0 && (
            <div className="chat-accordion mt-4 mb-4">
              <div className="fw-bold mb-2 text-muted">Recommended Questions</div>
              <Accordion defaultActiveKey={null}>
                {groupedQuestions.map(([category, questions], idx) => (
                  <Accordion.Item eventKey={String(idx)} key={category}>
                    <Accordion.Header>{category}</Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex flex-wrap gap-2">
                        {questions.map((q, i) => (
                          <button
                            key={i}
                            className="btn btn-outline btn-accordion-hover text-start btn-sm"
                            onClick={() => handleRecommendedClick(q.question)}
                            disabled={isBotResponding}
                          >
                            {q.question}
                          </button>
                        ))}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          )}

          {/* Scroll: Sohbet otomatik en alta scroll */}
          <div ref={bottomRef}></div>
        </div>
        <div className="card-footer p-0">
          <div className="position-relative w-100 px-3">
            <textarea
              ref={textareaRef}
              className="form-control p-2 pt-4 pe-5 border-0"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={connected ? 'Type a message...' : 'Connecting...'}
              onKeyDown={handleKeyDown}
              disabled={!connected || loading || isBotResponding}
              style={{
                minHeight: '60px',
                maxHeight: '100px',
                overflowY: 'auto',
                resize: 'none',
              }}
            ></textarea>
            <div className='d-flex justify-content-end align-items-center'
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                margin: '1rem',
                width: '270px'
              }}
            >
              {questionSent === true && (
                <button
                  className="btn btn-sm btn-light me-2 position-absolute top-0 bottom-0 my-auto"
                  style={{ left: 10, height: '36px' }}
                  onClick={toggleAccordion} // Using the new toggle function
                >
                  <i className="bi bi-question-circle"></i>
                  Recommended Questions
                </button>
              )}
            </div>
            <div className="d-flex justify-content-end gap-0 align-items-center"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  margin: '1rem',
                }}
            >
              <button
                className="btn btn-primary btn-sm p-4 text-end pull-right bg-brand rounded-circle"
                onClick={sendMessage}
                disabled={!connected || loading || isBotResponding || !inputText.trim()}
              >
                <img src="/media/send.png" alt="" width={20} height={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;